import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Send,
  CheckCircle2,
  Sparkles,
  Flame,
  Lock,
  Crown,
  Star,
  Shield,
  Zap,
  Trophy,
} from 'lucide-react';
import SiteFooter from '../components/sections/SiteFooter';
import SubmitTipButton from '../components/SubmitTipButton';
import TopNav from '../components/sections/TopNav';
import PageSeoTag from '../components/PageSeoTag';

const API = process.env.REACT_APP_BACKEND_URL;
const CARD_HERO =
  'https://customer-assets.emergentagent.com/job_d497ffe6-f7ac-4d46-ab2f-f65a2fd380b7/artifacts/kp6m8mm3_1000033065.png';

const CHASE_CARDS = [
  { n: 1, name: 'Kaz Hamad EX', tag: 'Only 5 in existence', halo: true },
  { n: 2, name: '???', tag: 'Ultra-elite tier' },
  { n: 3, name: '???', tag: 'Ultra-elite tier' },
  { n: 4, name: '???' },
  { n: 5, name: '???' },
  { n: 6, name: '???' },
  { n: 7, name: '???' },
  { n: 8, name: '???' },
  { n: 9, name: '???' },
  { n: 10, name: '???' },
];

const MID_CARDS = Array.from({ length: 9 }, (_, i) => `??? · #${11 + i}`);

const COMMON_CARDS = Array.from({ length: 11 }, (_, i) => `??? · #${20 + i}`);

const Stat = ({ value, label, accent = 'text-red-500' }) => (
  <div className="flex flex-col items-center justify-center text-center">
    <div className={`text-4xl md:text-5xl font-black tracking-tight ${accent} drop-shadow-[0_4px_18px_rgba(0,0,0,0.85)]`}>
      {value}
    </div>
    <div className="mt-1 text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-gray-400 font-black">
      {label}
    </div>
  </div>
);

const SectionEyebrow = ({ children }) => (
  <p className="text-[11px] tracking-[0.4em] uppercase text-yellow-400/90 font-black mb-2">
    {children}
  </p>
);

const GanglandCards = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'ganglandcards_launch' }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Sign-up failed. Please try again.');
      }
      setSuccess(true);
      setEmail('');
    } catch (err) {
      setError(err.message || 'Sign-up failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Sparks scattered around the hero card for atmosphere (memoised so positions are stable on re-render).
  const sparks = useMemo(
    () =>
      Array.from({ length: 18 }).map((_, i) => ({
        id: i,
        top: `${Math.random() * 95}%`,
        left: `${Math.random() * 95}%`,
        delay: `${(Math.random() * 4).toFixed(2)}s`,
        size: 4 + Math.round(Math.random() * 8),
      })),
    [],
  );

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden" data-testid="ganglandcards-page">
      <TopNav />
      <PageSeoTag pageKey="ganglandcards" />

      <header className="bg-zinc-900/80 border-b border-zinc-800 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-300 hover:text-white text-sm font-bold"
            data-testid="gc-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <span className="text-[11px] font-bold tracking-wider text-yellow-400 uppercase">
            Series 1
          </span>
        </div>
      </header>

      {/* ============= HERO ============= */}
      <section
        className="relative px-4 sm:px-6 lg:px-8 pt-8 pb-8 md:pt-14 md:pb-12"
        data-testid="gc-hero"
      >
        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <div className="text-center lg:text-left order-2 lg:order-1">
            <p className="text-[11px] font-bold uppercase tracking-wider text-yellow-400 mb-3">
              World First · Launch Soon
            </p>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight tracking-tight">
              The <span className="text-yellow-400">Gangland</span> Collection
            </h1>
            <p className="mt-3 text-sm md:text-base text-gray-400 tracking-wide">
              Melbourne Edition <span className="text-red-500 mx-1">·</span> Series 1
            </p>

            <p className="mt-6 italic text-gray-300 text-base md:text-lg max-w-xl mx-auto lg:mx-0">
              World First Trading Cards - styled after the iconic Pokémon format.
            </p>

            <p className="mt-5 text-gray-200 text-base md:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
              Only <span className="text-yellow-400 font-black">1,000 packs</span> will ever exist.{' '}
              <span className="text-white font-black">3 cards per pack.</span>{' '}
              <span className="text-red-400 font-black">$29.95.</span>
            </p>

            <p className="mt-4 text-sm md:text-base text-gray-400 max-w-xl mx-auto lg:mx-0">
              The same pack-opening thrill that has dominated global collecting culture for
              25+ years… now applied to Melbourne's rawest stories.
            </p>

            <p className="mt-6 text-xl md:text-2xl font-black tracking-tight text-white">
              The hunt starts <span className="text-red-500">soon.</span>
            </p>

            {/* Stat row */}
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md mx-auto lg:mx-0 border border-yellow-500/20 bg-zinc-950/70 rounded-xl p-4">
              <Stat value="1,000" label="Packs · Worldwide" accent="text-yellow-400" />
              <Stat value="30" label="Cards · Full Set" accent="text-white" />
              <Stat value="5" label="Secret Rares" accent="text-red-500" />
            </div>

            <a
              href="#notify"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-red-600 hover:bg-red-500 text-white font-black tracking-[0.2em] uppercase text-sm shadow-[0_10px_30px_-8px_rgba(220,38,38,0.7)] transition-colors"
              data-testid="gc-hero-cta"
            >
              <Bell className="h-4 w-4" />
              Notify Me At Launch
            </a>
          </div>

          {/* Card showcase */}
          <div className="order-1 lg:order-2 flex items-center justify-center w-full">
            <div className="relative text-center">
              {/* Floating sparks */}
              {sparks.map((s) => (
                <span
                  key={s.id}
                  aria-hidden="true"
                  className="absolute rounded-full bg-yellow-400 gc-spark pointer-events-none"
                  style={{
                    top: s.top,
                    left: s.left,
                    width: s.size,
                    height: s.size,
                    animationDelay: s.delay,
                  }}
                />
              ))}
              {/* Holographic glow plate */}
              <div
                aria-hidden="true"
                className="absolute -inset-6 rounded-3xl blur-2xl opacity-70"
                style={{
                  background:
                    'conic-gradient(from 0deg, rgba(234,179,8,0.55), rgba(220,38,38,0.45), rgba(168,85,247,0.35), rgba(234,179,8,0.55))',
                }}
              />
              <div className="relative gc-card-tilt gc-gold-pulse inline-block rounded-2xl overflow-hidden">
                <img
                  src={CARD_HERO}
                  alt="Gangland Collection Series 1 · Example card"
                  className="relative block w-[280px] sm:w-[320px] md:w-[360px] lg:w-[400px] rounded-2xl shadow-[0_30px_80px_-15px_rgba(0,0,0,0.9)] ring-1 ring-yellow-500/30"
                  data-testid="gc-hero-card"
                />
                {/* Specular sheen - clipped to card, feathered top/bottom */}
                <span aria-hidden="true" className="absolute inset-0 rounded-2xl gc-card-sheen pointer-events-none" />
              </div>
              <p className="mt-4 text-center text-[10px] uppercase tracking-[0.3em] text-gray-500">
                Example card · Final art & finish may differ
              </p>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes gc-spark {
            0%, 100% { opacity: 0; transform: scale(0.6); }
            50% { opacity: 0.95; transform: scale(1.2); box-shadow: 0 0 12px rgba(234,179,8,0.9); }
          }
          .gc-spark { animation: gc-spark 3.6s ease-in-out infinite; }

          @keyframes gc-tilt {
            0%, 100% { transform: rotate(-2deg) translateY(0); }
            50%      { transform: rotate(2deg) translateY(-16px); }
          }
          .gc-card-tilt { animation: gc-tilt 6s ease-in-out infinite; }

          @keyframes gc-sheen {
            0%   { transform: translateX(-110%); }
            100% { transform: translateX(110%); }
          }
          .gc-card-sheen {
            background: linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.22) 50%, transparent 65%);
            /* Feather the top and bottom edges so the sheen doesn't look cut off */
            -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 18%, #000 82%, transparent 100%);
                    mask-image: linear-gradient(180deg, transparent 0%, #000 18%, #000 82%, transparent 100%);
            animation: gc-sheen 4.5s ease-in-out infinite;
          }

          /* Gold-embossed pulse - converts the entire card to a rich gold treatment
             for a brief moment, then eases back to normal. */
          @keyframes gc-gold-pulse {
            0%, 100% {
              filter:
                sepia(0) saturate(1) hue-rotate(0deg) brightness(1) contrast(1)
                drop-shadow(0 0 0 rgba(234,179,8,0));
            }
            45%, 55% {
              filter:
                sepia(1) saturate(4.5) hue-rotate(-18deg) brightness(1.18) contrast(1.05)
                drop-shadow(0 0 28px rgba(234,179,8,0.9))
                drop-shadow(0 0 70px rgba(234,179,8,0.4));
            }
          }
          .gc-gold-pulse { animation: gc-gold-pulse 2.5s ease-in-out infinite; }
        `}</style>
      </section>

      {/* ============= STORY ============= */}
      <section className="relative px-4 sm:px-6 lg:px-8 py-8 md:py-12 border-y border-zinc-900" data-testid="gc-story">
        <div className="max-w-3xl mx-auto">
          <SectionEyebrow>The Story Behind The Cards</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-6">
            Two of the biggest cultural forces on the planet,{' '}
            <span className="text-yellow-400">fused into one collection.</span>
          </h2>

          <div className="space-y-5 text-gray-300 text-base md:text-[17px] leading-relaxed">
            <p>
              Pokémon cards proved one thing beyond doubt:{' '}
              <span className="text-white font-bold">people are obsessed with the hunt.</span> The
              thrill of ripping a pack, chasing the rare pull, building a set, and owning a piece of
              something culturally massive.
            </p>
            <p>True crime has the exact same grip on audiences right now.</p>
            <p className="text-white font-bold">
              The Gangland Collection brings both worlds together for the first time.
            </p>
            <p>
              Styled directly after the legendary Pokémon Trading Card Game layout - familiar name
              banners, central artwork, rarity symbols, and that addictive "what's inside?" feeling
              - but loaded with the real figures, crews and iconic locations from Melbourne's
              underworld as reported by Outlaw Media.
            </p>
            <p>
              This isn't nostalgia for nostalgia's sake. It's the proven collecting format that has
              collectors worldwide ripping packs for decades, now fused with the unfiltered stories
              everyone is hungry for.
            </p>
            <p className="text-lg md:text-xl font-black tracking-wide text-white">
              Raw. <span className="text-red-500">Limited.</span> Authentic.{' '}
              <span className="text-yellow-400">Unapologetic.</span>
            </p>
            <p>
              Whether you grew up collecting Pokémon, follow every Outlaw Media exclusive, or simply
              love the rush of the chase - this collection was built for you.
            </p>
          </div>
        </div>
      </section>

      {/* ============= WHAT'S INSIDE ============= */}
      <section className="relative px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="gc-whats-inside">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <SectionEyebrow>What's Inside Each Pack</SectionEyebrow>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">
              3 cards per pack. <span className="text-yellow-400">30 cards in the set.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                title: 'Chase Cards',
                range: '1–10',
                copy: 'The ultra-rares. The ones that will define collections.',
                icon: Trophy,
                border: 'border-yellow-500/60',
                bg: 'from-yellow-500/10 via-black to-black',
                accent: 'text-yellow-400',
              },
              {
                title: 'Mid Cards',
                range: '11–19',
                copy: 'Strong, meaningful pulls. Crews, operators, taskforces.',
                icon: Shield,
                border: 'border-zinc-500/40',
                bg: 'from-zinc-500/10 via-black to-black',
                accent: 'text-zinc-200',
              },
              {
                title: 'Common Cards',
                range: '20–30',
                copy: 'The foundation of the set - the people and places that shape the Melbourne story.',
                icon: Star,
                border: 'border-red-700/50',
                bg: 'from-red-700/10 via-black to-black',
                accent: 'text-red-400',
              },
            ].map(({ title, range, copy, icon: Icon, border, bg, accent }) => (
              <div
                key={title}
                className={`rounded-2xl border ${border} bg-gradient-to-br ${bg} p-5`}
                data-testid={`gc-tier-${title.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <Icon className={`h-5 w-5 ${accent}`} />
                  <span className={`text-[10px] font-black tracking-[0.3em] uppercase ${accent}`}>
                    {range}
                  </span>
                </div>
                <h3 className={`text-xl font-black uppercase tracking-wide ${accent}`}>{title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>

          {/* Scarcity ribbon */}
          <div className="mt-10 rounded-2xl border-2 border-yellow-500/40 bg-gradient-to-br from-black via-yellow-950/20 to-black p-6 md:p-8 text-center">
            <p className="text-[11px] font-black tracking-[0.35em] uppercase text-yellow-400 mb-2">
              Strictly Limited
            </p>
            <p className="text-2xl md:text-4xl font-black tracking-tight">
              <span className="text-white">1,000 packs worldwide.</span>{' '}
              <span className="text-red-500">$29.95 each.</span>
            </p>
            <p className="mt-3 text-sm md:text-base text-gray-400 max-w-xl mx-auto">
              The same scarcity psychology that makes Pokémon packs addictive - now applied to
              Melbourne gangland history.{' '}
              <span className="text-white font-bold">
                Once these 1,000 packs are gone, they're gone forever.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* ============= CARD DESIGN ============= */}
      <section
        className="relative px-4 sm:px-6 lg:px-8 py-8 md:py-12 border-y border-zinc-900 bg-zinc-950/40"
        data-testid="gc-card-design"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3 order-2 lg:order-1">
            <SectionEyebrow>Card Design</SectionEyebrow>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-5">
              Pokémon-inspired. <span className="text-red-500">Melbourne edge.</span>
            </h2>
            <p className="text-gray-300 text-base md:text-[17px] leading-relaxed">
              The physical cards deliberately follow the iconic structure collectors know and love.
            </p>

            <ul className="mt-5 space-y-2.5 text-gray-300 text-base">
              {[
                'Bold top name banner',
                'Striking central artwork (photography and illustration)',
                'Clear rarity tier symbols',
                'Familiar layout and finish quality - foil and holographic treatments on chase and secret rares',
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-1 inline-block h-2 w-2 rounded-full bg-yellow-400 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <p className="mt-6 text-gray-300 text-base md:text-[17px] leading-relaxed">
              But with a distinctly Outlaw twist. Instead of HP and attacks, you'll find{' '}
              <span className="text-yellow-400 font-black">"Notoriety Level"</span>,{' '}
              <span className="text-yellow-400 font-black">"Crew Affiliation"</span>,{' '}
              <span className="text-yellow-400 font-black">"Heat Index"</span> and authentic story
              references pulled straight from Outlaw Media reporting.
            </p>

            <p className="mt-5 text-gray-400 text-sm md:text-base leading-relaxed">
              Pokémon showed the world how powerful the pack-opening ritual and rarity chase can be.
              True crime proves people crave the unfiltered truth.{' '}
              <span className="text-white font-bold">We combined both.</span>
            </p>
          </div>

          {/* Smaller card on this section */}
          <div className="lg:col-span-2 order-1 lg:order-2 flex justify-center">
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute -inset-4 rounded-2xl blur-xl opacity-60"
                style={{
                  background:
                    'radial-gradient(circle at 50% 50%, rgba(234,179,8,0.6), rgba(220,38,38,0.3) 60%, transparent 80%)',
                }}
              />
              <img
                src={CARD_HERO}
                alt="Gangland Collection card example"
                className="relative w-[230px] md:w-[280px] rounded-xl ring-1 ring-yellow-500/30 shadow-[0_18px_50px_-10px_rgba(0,0,0,0.85)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============= RARITY SYSTEM ============= */}
      <section className="relative px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="gc-rarity">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <SectionEyebrow>The Rarity System</SectionEyebrow>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">
              Chase your <span className="text-yellow-400">fortune.</span>
            </h2>
          </div>

          {/* Chase cards */}
          <div className="rounded-2xl border-2 border-yellow-500/50 bg-gradient-to-br from-yellow-500/5 via-black to-black p-6 md:p-8 mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="h-5 w-5 text-yellow-400" />
              <h3 className="text-2xl font-black uppercase tracking-wide text-yellow-400">
                Chase Cards · 1–10
              </h3>
            </div>
            <p className="text-gray-300 mb-5">
              Ranked 10 down to 1 in scarcity. These are the cards collectors will be hunting.
            </p>

            <div className="grid sm:grid-cols-2 gap-2.5">
              {CHASE_CARDS.map((c) => (
                <div
                  key={c.n}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${
                    c.halo
                      ? 'border-yellow-400/80 bg-yellow-500/10 shadow-[0_0_30px_-8px_rgba(234,179,8,0.55)]'
                      : 'border-zinc-800 bg-zinc-950/60'
                  }`}
                  data-testid={`gc-chase-${c.n}`}
                >
                  <span
                    className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-black text-sm ${
                      c.halo
                        ? 'bg-yellow-400 text-black'
                        : c.n <= 3
                        ? 'bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-400/40'
                        : 'bg-zinc-900 text-yellow-300 ring-1 ring-yellow-500/20'
                    }`}
                  >
                    #{c.n}
                  </span>
                  <div className="min-w-0">
                    <p className="text-white font-black truncate">{c.name}</p>
                    {c.tag && (
                      <p
                        className={`text-[10px] uppercase tracking-[0.2em] ${
                          c.halo ? 'text-yellow-700' : 'text-gray-500'
                        }`}
                      >
                        {c.tag}
                      </p>
                    )}
                  </div>
                  {c.halo && <Flame className="h-4 w-4 text-red-500 ml-auto shrink-0" />}
                </div>
              ))}
            </div>
          </div>

          {/* Mid + Common - side by side on md */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-zinc-700/60 bg-zinc-950/60 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-5 w-5 text-zinc-200" />
                <h3 className="text-xl font-black uppercase tracking-wide text-white">
                  Mid Cards · 11–19
                </h3>
              </div>
              <ul className="space-y-1.5">
                {MID_CARDS.map((m) => (
                  <li key={m} className="text-gray-300 text-sm flex items-start gap-2">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-zinc-400 shrink-0" />
                    {m}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-red-900/50 bg-zinc-950/60 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Star className="h-5 w-5 text-red-400" />
                <h3 className="text-xl font-black uppercase tracking-wide text-white">
                  Common Cards · 20–30
                </h3>
              </div>
              <ul className="space-y-1.5">
                {COMMON_CARDS.map((c) => (
                  <li key={c} className="text-gray-300 text-sm flex items-start gap-2">
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-red-600 shrink-0" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Secret rares */}
          <div className="mt-8 rounded-2xl border-2 border-red-700/60 bg-gradient-to-br from-red-950/40 via-black to-black p-6 md:p-8 text-center relative overflow-hidden">
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                backgroundImage:
                  'radial-gradient(circle, rgba(234,179,8,0.7) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
              }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600/15 border border-red-500/50 mb-3">
                <Lock className="h-3.5 w-3.5 text-red-400" />
                <span className="text-[11px] font-black tracking-[0.3em] uppercase text-red-300">
                  Secret Rares
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-black tracking-tight text-white">
                Only <span className="text-yellow-400">5 autographed cards</span> hidden across the
                entire <span className="text-red-500">1,000-pack run.</span>
              </h3>
              <p className="mt-3 text-gray-400 max-w-xl mx-auto text-sm md:text-base">
                Pull one and you own something genuinely unrepeatable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============= WHY IT MATTERS ============= */}
      <section
        className="relative px-4 sm:px-6 lg:px-8 py-8 md:py-12 border-y border-zinc-900"
        data-testid="gc-why"
      >
        <div className="max-w-3xl mx-auto text-center">
          <SectionEyebrow>Why This Collection Matters</SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-6">
            This is a <span className="text-yellow-400">world first.</span>
          </h2>
          <div className="space-y-5 text-gray-300 text-base md:text-[17px] leading-relaxed">
            <p>
              It takes the most successful collectable card format in history - the one that has
              created global collecting frenzies for over two decades - and applies it to the real
              Melbourne stories that mainstream outlets won't touch.
            </p>
            <p className="text-white font-black text-lg md:text-xl">
              The Pokémon-style thrill of the hunt… meets the raw truth of the streets.
            </p>
            <p>
              For the Outlaw Media community that has supported the real reporting from the
              beginning, this is your chance to own a tangible, limited piece of that history - in
              a format designed to be collected, traded, displayed and chased.
            </p>
          </div>
        </div>
      </section>

      {/* ============= NOTIFY ME ============= */}
      <section
        id="notify"
        className="relative px-4 sm:px-6 lg:px-8 py-10 md:py-14"
        data-testid="gc-notify"
      >
        <div className="max-w-2xl mx-auto">
          <div className="relative rounded-2xl border-2 border-yellow-500/50 bg-gradient-to-br from-yellow-950/30 via-black to-red-950/30 p-7 md:p-10 text-center overflow-hidden shadow-[0_30px_80px_-20px_rgba(234,179,8,0.45)]">
            <div
              aria-hidden="true"
              className="absolute -top-24 -left-24 w-64 h-64 rounded-full blur-3xl opacity-40"
              style={{ background: 'radial-gradient(circle, rgba(234,179,8,0.55), transparent 70%)' }}
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-40"
              style={{ background: 'radial-gradient(circle, rgba(220,38,38,0.55), transparent 70%)' }}
            />

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600/15 border border-red-500/50 mb-5">
                <Zap className="h-3.5 w-3.5 text-red-400" />
                <span className="text-[11px] font-black tracking-[0.3em] text-red-300 uppercase">
                  Be First When They Drop
                </span>
              </div>

              <h2 className="text-3xl md:text-5xl font-black tracking-tight text-white">
                LIMITED TO <span className="text-yellow-400">1,000 PACKS.</span>
              </h2>
              <p className="mt-3 text-base md:text-lg text-gray-300 max-w-md mx-auto">
                First in, first served. The same collecting energy that built Pokémon empires -
                now for Melbourne's real stories.
              </p>

              {success ? (
                <div
                  className="mt-6 inline-flex items-center gap-2 bg-emerald-900/30 border border-emerald-500/50 text-emerald-300 px-5 py-3 rounded-lg font-bold"
                  data-testid="gc-signup-success"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  Check your inbox and tap the confirm link - that's the last step.
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="mt-7 flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
                  data-testid="gc-signup-form"
                >
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="gc-email-input"
                    className="flex-1 bg-black/60 border border-yellow-700/40 text-white px-4 py-3 rounded text-sm focus:outline-none focus:border-yellow-400 placeholder-gray-500"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="gc-signup-submit"
                    className="bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-black tracking-[0.2em] px-5 py-3 rounded transition-colors flex items-center justify-center gap-2 uppercase text-sm"
                  >
                    <Send className="h-4 w-4" />
                    {submitting ? 'Signing up...' : 'Notify Me'}
                  </button>
                </form>
              )}
              {error && (
                <p className="mt-3 text-red-400 text-sm" data-testid="gc-signup-error">
                  {error}
                </p>
              )}
              <p className="mt-5 text-[10px] text-gray-500 tracking-[0.2em] uppercase max-w-md mx-auto">
                We respect your inbox. You'll only hear from us about this drop and future Outlaw
                releases.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
      <SubmitTipButton />
    </div>
  );
};

export default GanglandCards;
